import React, { useState, useEffect, useCallback } from 'react';
import CloudStaffSidebar from './CloudStaffSidebar';
import { getAuthHeader } from '../../../utils/auth';
import { getApiEndpoint } from '../../../config/api';
import {
  FaUsers, FaSignInAlt, FaCalendarDay, FaUserPlus,
  FaGlobe, FaSync, FaSearch, FaTimes, FaCircle,
  FaMapMarkerAlt, FaDesktop, FaMobileAlt, FaClock
} from 'react-icons/fa';
import './UserActivityPage.css';

const POLL_INTERVAL = 60_000; // refresh every 60s

const timeAgo = (ts) => {
  if (!ts) return '—';
  const diff = Date.now() - new Date(ts).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'Just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
};

const isOnline = (lastActive) => {
  if (!lastActive) return false;
  return Date.now() - new Date(lastActive).getTime() < 10 * 60 * 1000; // 10 min
};

const roleColor = (role) => {
  const map = {
    super_admin: '#7c3aed',
    admin: '#1A80E5',
    cloud_staff: '#0ea5e9',
    staff: '#10b981',
    viewer: '#6b7280',
    contractor: '#f59e0b',
    building_manager: '#8b5cf6',
  };
  return map[role] || '#6b7280';
};

const deviceIcon = (ua) => {
  if (!ua) return null;
  const lower = ua.toLowerCase();
  if (lower.includes('mobile') || lower.includes('android') || lower.includes('iphone')) return FaMobileAlt;
  return FaDesktop;
};

const initials = (first, last) => {
  return `${(first || '')[0] || ''}${(last || '')[0] || ''}`.toUpperCase() || '?';
};

export default function UserActivityPage() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [feed, setFeed] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [lastRefresh, setLastRefresh] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [tab, setTab] = useState('users'); // 'users' | 'feed'

  const fetchAll = useCallback(async (isManual = false) => {
    if (isManual) setRefreshing(true);
    try {
      const headers = getAuthHeader();
      const [statsRes, usersRes, feedRes, locRes] = await Promise.all([
        fetch(getApiEndpoint('/api/cloud-staff/user-activity/stats'), { headers }),
        fetch(getApiEndpoint(`/api/cloud-staff/user-activity/users?search=${encodeURIComponent(search)}&limit=100`), { headers }),
        fetch(getApiEndpoint('/api/cloud-staff/user-activity/feed?limit=50'), { headers }),
        fetch(getApiEndpoint('/api/cloud-staff/user-activity/locations'), { headers }),
      ]);
      if (statsRes.ok) setStats(await statsRes.json());
      if (usersRes.ok) { const d = await usersRes.json(); setUsers(d.users || []); }
      if (feedRes.ok) { const d = await feedRes.json(); setFeed(d.feed || []); }
      if (locRes.ok) { const d = await locRes.json(); setLocations(d.locations || []); }
      setLastRefresh(new Date());
    } catch (err) {
      console.error('UserActivityPage fetch error:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [search]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    const t = setInterval(() => fetchAll(), POLL_INTERVAL);
    return () => clearInterval(t);
  }, [fetchAll]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
  };

  const onlineCount = users.filter(u => isOnline(u.last_active_at)).length;

  return (
    <div className="uap-shell">
      <CloudStaffSidebar active="user-activity" />

      <main className="uap-main">
        {/* Header */}
        <div className="uap-header">
          <div className="uap-header-left">
            <div className="uap-header-icon"><FaUsers /></div>
            <div>
              <h1 className="uap-title">User Activity</h1>
              <p className="uap-subtitle">Login history, active sessions, and geographic distribution</p>
            </div>
          </div>
          <div className="uap-header-right">
            {lastRefresh && (
              <span className="uap-last-refresh">
                <FaClock /> Updated {timeAgo(lastRefresh)}
              </span>
            )}
            <button
              className={`uap-refresh-btn ${refreshing ? 'spinning' : ''}`}
              onClick={() => fetchAll(true)}
              disabled={refreshing}
            >
              <FaSync /> Refresh
            </button>
          </div>
        </div>

        {/* Stat cards */}
        <div className="uap-stats">
          <div className="uap-stat-card uap-stat-online">
            <div className="uap-stat-icon"><FaCircle /></div>
            <div className="uap-stat-body">
              <div className="uap-stat-value">{loading ? '—' : onlineCount}</div>
              <div className="uap-stat-label">Online Now</div>
              <div className="uap-stat-sub">Active in last 10 min</div>
            </div>
          </div>
          <div className="uap-stat-card">
            <div className="uap-stat-icon uap-stat-icon-blue"><FaSignInAlt /></div>
            <div className="uap-stat-body">
              <div className="uap-stat-value">{loading ? '—' : stats?.active24h ?? 0}</div>
              <div className="uap-stat-label">Active (24h)</div>
              <div className="uap-stat-sub">Unique logins today</div>
            </div>
          </div>
          <div className="uap-stat-card">
            <div className="uap-stat-icon uap-stat-icon-purple"><FaCalendarDay /></div>
            <div className="uap-stat-body">
              <div className="uap-stat-value">{loading ? '—' : stats?.active7d ?? 0}</div>
              <div className="uap-stat-label">Active (7d)</div>
              <div className="uap-stat-sub">Last 7 days</div>
            </div>
          </div>
          <div className="uap-stat-card">
            <div className="uap-stat-icon uap-stat-icon-amber"><FaSignInAlt /></div>
            <div className="uap-stat-body">
              <div className="uap-stat-value">{loading ? '—' : stats?.loginsToday ?? 0}</div>
              <div className="uap-stat-label">Logins Today</div>
              <div className="uap-stat-sub">Total login events</div>
            </div>
          </div>
          <div className="uap-stat-card">
            <div className="uap-stat-icon uap-stat-icon-teal"><FaUsers /></div>
            <div className="uap-stat-body">
              <div className="uap-stat-value">{loading ? '—' : stats?.totalUsers ?? 0}</div>
              <div className="uap-stat-label">Total Users</div>
              <div className="uap-stat-sub">{stats?.newUsersToday ?? 0} new today</div>
            </div>
          </div>
        </div>

        {/* Main content */}
        <div className="uap-content">
          {/* Left: users / feed */}
          <div className="uap-panel-main">
            {/* Tab bar + search */}
            <div className="uap-panel-header">
              <div className="uap-tabs">
                <button
                  className={`uap-tab ${tab === 'users' ? 'active' : ''}`}
                  onClick={() => setTab('users')}
                >
                  <FaUsers /> Users
                  <span className="uap-tab-count">{users.length}</span>
                </button>
                <button
                  className={`uap-tab ${tab === 'feed' ? 'active' : ''}`}
                  onClick={() => setTab('feed')}
                >
                  <FaSignInAlt /> Login Feed
                  <span className="uap-tab-count">{feed.length}</span>
                </button>
              </div>

              {tab === 'users' && (
                <form className="uap-search" onSubmit={handleSearch}>
                  <FaSearch className="uap-search-icon" />
                  <input
                    className="uap-search-input"
                    placeholder="Search users..."
                    value={searchInput}
                    onChange={e => setSearchInput(e.target.value)}
                  />
                  {searchInput && (
                    <button type="button" className="uap-search-clear" onClick={() => { setSearchInput(''); setSearch(''); }}>
                      <FaTimes />
                    </button>
                  )}
                </form>
              )}
            </div>

            {/* Users table */}
            {tab === 'users' && (
              <div className="uap-table-wrap">
                {loading ? (
                  <div className="uap-loading">Loading users…</div>
                ) : users.length === 0 ? (
                  <div className="uap-empty">No activity recorded yet. Users will appear here after their first login.</div>
                ) : (
                  <table className="uap-table">
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Role</th>
                        <th>Last Login</th>
                        <th>Last Active</th>
                        <th>IP Address</th>
                        <th>Location</th>
                        <th>Logins</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map(u => {
                        const online = isOnline(u.last_active_at);
                        const DeviceIcon = deviceIcon(u.last_user_agent);
                        return (
                          <tr key={u.id} className={online ? 'uap-row-online' : ''}>
                            <td>
                              <div className="uap-user-cell">
                                <div className="uap-avatar" style={{ background: roleColor(u.role) }}>
                                  {initials(u.first_name, u.last_name)}
                                  {online && <span className="uap-online-dot" />}
                                </div>
                                <div>
                                  <div className="uap-user-name">
                                    {u.first_name} {u.last_name}
                                  </div>
                                  <div className="uap-user-email">{u.email}</div>
                                </div>
                              </div>
                            </td>
                            <td>
                              <span className="uap-role-badge" style={{ background: roleColor(u.role) + '18', color: roleColor(u.role) }}>
                                {u.role || 'viewer'}
                              </span>
                            </td>
                            <td className="uap-cell-muted">
                              {u.last_login_at ? (
                                <span title={new Date(u.last_login_at).toLocaleString()}>
                                  {timeAgo(u.last_login_at)}
                                </span>
                              ) : '—'}
                            </td>
                            <td>
                              {u.last_active_at ? (
                                <span className={`uap-active-tag ${online ? 'online' : ''}`} title={new Date(u.last_active_at).toLocaleString()}>
                                  {online ? '● Online' : timeAgo(u.last_active_at)}
                                </span>
                              ) : '—'}
                            </td>
                            <td className="uap-cell-mono">
                              <div className="uap-ip-cell">
                                {DeviceIcon && <DeviceIcon className="uap-device-icon" />}
                                {u.last_ip || '—'}
                              </div>
                            </td>
                            <td>
                              {u.geo_country ? (
                                <div className="uap-location-cell">
                                  <span className="uap-flag">{u.geo_flag}</span>
                                  <span>{u.geo_city ? `${u.geo_city}, ` : ''}{u.geo_country}</span>
                                </div>
                              ) : '—'}
                            </td>
                            <td className="uap-cell-center">
                              <span className="uap-login-count">{u.login_count || 0}</span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* Activity feed */}
            {tab === 'feed' && (
              <div className="uap-feed">
                {loading ? (
                  <div className="uap-loading">Loading feed…</div>
                ) : feed.length === 0 ? (
                  <div className="uap-empty">No login events recorded yet.</div>
                ) : (
                  feed.map(event => {
                    const DeviceIcon = deviceIcon(event.user_agent);
                    return (
                      <div key={event.id} className="uap-feed-item">
                        <div className="uap-feed-avatar" style={{ background: roleColor(event.role) }}>
                          {initials(event.first_name, event.last_name)}
                        </div>
                        <div className="uap-feed-body">
                          <div className="uap-feed-top">
                            <span className="uap-feed-name">{event.first_name} {event.last_name}</span>
                            <span className="uap-feed-email">{event.email}</span>
                            <span className="uap-feed-event">signed in</span>
                          </div>
                          <div className="uap-feed-meta">
                            {DeviceIcon && <DeviceIcon className="uap-feed-meta-icon" />}
                            <span className="uap-cell-mono">{event.ip_address}</span>
                            {event.geo_country && (
                              <>
                                <span className="uap-feed-dot">·</span>
                                <span>{event.geo_flag} {event.geo_city ? `${event.geo_city}, ` : ''}{event.geo_country}</span>
                              </>
                            )}
                            <span className="uap-feed-dot">·</span>
                            <span title={new Date(event.created_at).toLocaleString()}>
                              {timeAgo(event.created_at)}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>

          {/* Right: location breakdown */}
          <div className="uap-panel-side">
            <div className="uap-side-header">
              <FaGlobe className="uap-side-header-icon" />
              <span>Geographic Distribution</span>
            </div>
            {loading ? (
              <div className="uap-loading">Loading…</div>
            ) : locations.length === 0 ? (
              <div className="uap-empty uap-empty-sm">No location data yet</div>
            ) : (
              <div className="uap-locations">
                {locations.map((loc, i) => {
                  const maxCount = locations[0]?.unique_users || 1;
                  const pct = Math.round((loc.unique_users / maxCount) * 100);
                  return (
                    <div key={i} className="uap-location-row">
                      <div className="uap-location-left">
                        <span className="uap-location-flag">{loc.flag || '🌍'}</span>
                        <span className="uap-location-name">{loc.country || 'Unknown'}</span>
                      </div>
                      <div className="uap-location-bar-wrap">
                        <div className="uap-location-bar" style={{ width: `${pct}%` }} />
                      </div>
                      <div className="uap-location-right">
                        <span className="uap-location-users">{loc.unique_users}</span>
                        <span className="uap-location-logins">{loc.login_count} logins</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Map placeholder pin list */}
            <div className="uap-side-header uap-side-header-mt">
              <FaMapMarkerAlt className="uap-side-header-icon" />
              <span>Recent IP Locations</span>
            </div>
            <div className="uap-ip-list">
              {feed.slice(0, 8).map((event, i) => (
                <div key={i} className="uap-ip-row">
                  <span className="uap-ip-flag">{event.geo_flag || '🌍'}</span>
                  <div className="uap-ip-info">
                    <span className="uap-ip-addr">{event.ip_address || '—'}</span>
                    <span className="uap-ip-loc">
                      {event.geo_city ? `${event.geo_city}, ` : ''}{event.geo_country || 'Unknown'}
                    </span>
                  </div>
                  <span className="uap-ip-time">{timeAgo(event.created_at)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
